import React, { Fragment, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Input, Label,
         FormGroup, Alert, Table, Badge, Spinner } from 'reactstrap';
import { UploadCloud } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs } from '../../../AbstractElements';
import { parseFile, commitParse, describeError, formatInr } from './purchasesApi';

const PurchaseUpload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [piRef, setPiRef] = useState('');
  const [piDate, setPiDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setPreview(null); setResult(null); setError(null); };

  const handleParse = async () => {
    if (!file) return;
    setBusy(true); reset();
    try {
      setPreview(await parseFile(file, { piRef, piDate }));
    } catch (e) {
      setError(describeError(e, 'Could not read that file.'));
    } finally { setBusy(false); }
  };

  const handleCommit = async () => {
    setBusy(true); setError(null);
    try {
      setResult(await commitParse(preview.token, 'replace'));
      setPreview(null);
    } catch (e) {
      setError(describeError(e, 'Could not save those invoices.'));
    } finally { setBusy(false); }
  };

  const replacing = preview ? preview.invoices.filter((i) => i.existing) : [];

  return (
    <Fragment>
      <Breadcrumbs parent="Purchases" title="Upload PI" mainTitle="Upload PI" />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="py-3">
                <h5 className="mb-0">Upload a purchase invoice</h5>
                <small className="text-muted">
                  Either the consolidated cost sheet (every PI in one file) or a single PI.
                  The format is detected automatically — nothing is saved until you review it.
                </small>
              </CardHeader>
              <CardBody>
                {error && <Alert color="danger">{error}</Alert>}

                {result && (
                  <Alert color="success">
                    <strong>Saved.</strong>{' '}
                    {result.created.length > 0 && <>Added {result.created.length} PI(s): {result.created.join(', ')}. </>}
                    {result.updated.length > 0 && <>Replaced {result.updated.length}: {result.updated.join(', ')}. </>}
                    {result.itemsInserted} cost lines recorded.
                    <div className="mt-2">
                      <Button size="sm" color="primary" onClick={() => navigate('/purchases')}>View dashboard</Button>{' '}
                      <Button size="sm" color="secondary" outline onClick={() => { setResult(null); setFile(null); }}>Upload another</Button>
                    </div>
                  </Alert>
                )}

                {!preview && !result && (
                  <Fragment>
                    <Row className="g-3">
                      <Col md="12">
                        <label className="rf-dropzone d-block text-center p-4" style={{ cursor: 'pointer', border: '1px dashed #cfc6b8', borderRadius: 4 }}>
                          <UploadCloud size={28} className="mb-2 text-muted" />
                          <div>{file ? <strong>{file.name}</strong> : 'Click to choose an .xlsx file'}</div>
                          <Input type="file" accept=".xlsx,.xls" className="d-none"
                                 onChange={(e) => { setFile(e.target.files[0] || null); reset(); }} />
                        </label>
                      </Col>
                    </Row>
                    <Row className="g-3 mt-1">
                      <Col md="4">
                        <FormGroup>
                          <Label className="mb-1">PI reference</Label>
                          <Input value={piRef} onChange={(e) => setPiRef(e.target.value)} placeholder="e.g. PI 93" />
                          <small className="text-muted">Single-PI files only — the consolidated sheet carries its own.</small>
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label className="mb-1">PI date</Label>
                          <Input type="date" value={piDate} onChange={(e) => setPiDate(e.target.value)} />
                        </FormGroup>
                      </Col>
                      <Col md="4" className="d-flex align-items-end">
                        <Button color="primary" disabled={!file || busy} onClick={handleParse}>
                          {busy ? <Spinner size="sm" /> : 'Review file'}
                        </Button>
                      </Col>
                    </Row>
                  </Fragment>
                )}

                {preview && (
                  <Fragment>
                    <Alert color={replacing.length ? 'warning' : 'info'}>
                      Read <strong>{preview.summary.invoiceCount}</strong> invoice(s),{' '}
                      <strong>{preview.summary.itemCount}</strong> cost lines across{' '}
                      <strong>{preview.summary.distinctStyles}</strong> styles.{' '}
                      {preview.summary.unmatchedStyles.length > 0 && (
                        <>{preview.summary.unmatchedStyles.length} style(s) are not in the catalogue — their costs are still recorded.</>
                      )}
                      {replacing.length > 0 && (
                        <div className="mt-2">
                          <strong>{replacing.map((i) => i.piRef).join(', ')}</strong>{' '}
                          already exist and will be replaced with what is in this file.
                        </div>
                      )}
                    </Alert>

                    <div className="table-responsive mb-3">
                      <Table className="table-bordered" size="sm">
                        <thead>
                          <tr><th>PI</th><th>Date</th><th className="text-end">Lines</th><th className="text-end">Matched</th><th className="text-end">Unmatched</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                          {preview.invoices.map((inv) => (
                            <tr key={inv.piRef}>
                              <td><strong>{inv.piRef}</strong></td>
                              <td>{inv.piDate}</td>
                              <td className="text-end">{inv.itemCount}</td>
                              <td className="text-end">{inv.matchedCount}</td>
                              <td className="text-end">{inv.unmatchedCount || '—'}</td>
                              <td>{inv.existing
                                ? <Badge color="warning" pill>replaces {inv.existing.line_count} lines</Badge>
                                : <Badge color="success" pill>new</Badge>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {preview.warnings.length > 0 && (
                      <Alert color="warning">
                        <strong>{preview.warnings.length} row(s) need attention.</strong>
                        <ul className="mb-0 mt-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                          {preview.warnings.slice(0, 40).map((w, i) => (
                            <li key={i}><small>Row {w.row}{w.sku ? ` (${w.sku})` : ''}: {w.issues.join('; ')}</small></li>
                          ))}
                          {preview.warnings.length > 40 && <li><small>…and {preview.warnings.length - 40} more</small></li>}
                        </ul>
                      </Alert>
                    )}

                    <Button color="primary" disabled={busy} onClick={handleCommit}>
                      {busy ? <Spinner size="sm" /> : `Save ${preview.summary.itemCount} cost lines`}
                    </Button>{' '}
                    <Button color="secondary" outline disabled={busy} onClick={reset}>Cancel</Button>
                  </Fragment>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default PurchaseUpload;
